/*
  Pixel Burn — collection card review stars (2026-09-30)

  Why this exists: REVIEWS.io's own rating-snippet script fills in the stars once,
  for whatever cards are in the DOM when it runs. Our collection grids lazy-load,
  so everything past the first handful never gets stars. Their "Listen for Page
  Changes" setting does not reach their script (ratingSnippetObserver stays false).

  This fills in only the cards they leave blank. It reads live ratings from the
  same endpoint their widget uses, so new reviews appear as soon as they publish.
  Nothing is cached or stored in the theme. If the request fails, cards stay blank
  exactly as they are today — it never breaks the layout.

  Placeholders come from snippets/reviewsio-rating-snippet.liquid, whose data-sku is
  "productId;handle;sku1;sku2;variantId1;variantId2".
*/
(function () {
  'use strict';

  var ENDPOINT = 'https://api.reviews.io/product/rating-batch';
  var STAR_COLOR = '#FBBC05'; // matches the Star Color in the REVIEWS.io app embed
  var TEXT_COLOR = '#1F2937'; // matches the Text Color in the app embed
  var PLURAL = 'Reviews';
  var SINGULAR = 'Review';
  var MAX_QUERY = 7000; // their script switches to POST past 8000; stay well under
  var FIRST_RUN_DELAY = 1800; // let REVIEWS.io have first go at the initial cards

  function store() {
    return (typeof window.reviewsIoStore === 'string' && window.reviewsIoStore) ||
      window.location.hostname;
  }

  // A card is ours to fill only if REVIEWS.io hasn't claimed it and it's still empty.
  function unfilled() {
    var out = [];
    var all = document.querySelectorAll('.ruk_rating_snippet');
    for (var i = 0; i < all.length; i++) {
      var n = all[i];
      if (n.getAttribute('data-retrieved')) continue; // theirs
      if (n.getAttribute('data-pb-state')) continue;  // already handled by us
      if (n.innerHTML.trim()) continue;               // already has content
      if (!(n.getAttribute('data-sku') || '').trim()) continue;
      out.push(n);
    }
    return out;
  }

  function idsOf(node) {
    return (node.getAttribute('data-sku') || '')
      .split(';')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  // Same rounding REVIEWS.io use: quarter stars, 100/75/50/25/0 per star.
  function starsHtml(rating) {
    var r = Math.round(rating * 4) / 4;
    var html = '';
    for (var i = 1; i <= 5; i++) {
      var pct = r >= i ? 100 : r >= i - 0.25 ? 75 : r >= i - 0.5 ? 50 : r >= i - 0.75 ? 25 : 0;
      html += '<i aria-hidden="true" class="ruk-icon-percentage-star--' + pct + '"></i>';
    }
    return html;
  }

  function paint(node, average, count) {
    if (!count || count < 1) {
      node.setAttribute('data-pb-state', 'none'); // no reviews yet — leave blank
      return;
    }
    var word = count === 1 ? SINGULAR : PLURAL;
    var label = (Math.round(average * 100) / 100).toFixed(2) + ' Stars - ' + count + ' ' + word;
    node.innerHTML = starsHtml(average) +
      '<span aria-hidden="true" class="ruk-rating-snippet-count" style="color:' + TEXT_COLOR + '">' +
      count + ' ' + word + '</span>';
    node.style.color = STAR_COLOR;
    node.setAttribute('title', label);
    node.setAttribute('aria-label', label);
    node.setAttribute('data-pb-state', 'filled');
  }

  // Their matching rules: compare on sku, or any value in the item's ';'-separated
  // lookup field. Dedupe by sku so a product isn't counted twice.
  function ratingFor(ids, items) {
    var wanted = {};
    ids.forEach(function (id) { wanted[id.toLowerCase()] = true; });

    var seen = {}, weighted = 0, total = 0;
    items.forEach(function (item) {
      if (!item || !item.sku) return;
      var sku = String(item.sku).trim().toLowerCase();
      if (seen[sku]) return;

      var match = !!wanted[sku];
      if (!match && item.lookup) {
        match = String(item.lookup).toLowerCase().split(';').some(function (alt) {
          return wanted[alt.trim()];
        });
      }
      if (!match) return;

      var count = parseFloat(item.num_ratings) || 0;
      weighted += (parseFloat(item.average_rating) || 0) * count;
      total += count;
      seen[sku] = true;
    });

    return { average: total ? weighted / total : 0, count: total };
  }

  function chunk(nodes) {
    var batches = [], current = [], length = 0;
    nodes.forEach(function (node) {
      var size = idsOf(node).join(';').length + 1;
      if (current.length && length + size > MAX_QUERY) {
        batches.push(current); current = []; length = 0;
      }
      current.push(node); length += size;
    });
    if (current.length) batches.push(current);
    return batches;
  }

  function loadBatch(nodes) {
    var ids = {};
    nodes.forEach(function (n) { idsOf(n).forEach(function (id) { ids[id] = true; }); });
    var query = Object.keys(ids).join(';');
    if (!query) return Promise.resolve();

    // Claim these now so an observer tick mid-flight doesn't request them twice.
    nodes.forEach(function (n) { n.setAttribute('data-pb-state', 'loading'); });

    var url = ENDPOINT + '?sku=' + encodeURIComponent(query) +
      '&store=' + encodeURIComponent(store()) + '&lang=en';

    return fetch(url, { credentials: 'omit' })
      .then(function (res) { return res.ok ? res.json() : []; })
      .then(function (items) {
        if (!Array.isArray(items)) items = [];
        nodes.forEach(function (node) {
          node.removeAttribute('data-pb-state');
          var r = ratingFor(idsOf(node), items);
          paint(node, r.average, r.count);
        });
      })
      .catch(function () {
        // Fail silently: clear our claim, leave the cards blank as they are today.
        nodes.forEach(function (n) { n.removeAttribute('data-pb-state'); });
      });
  }

  var running = false, queued = false;

  function run() {
    if (running) { queued = true; return; }
    var todo = unfilled();
    if (!todo.length) return;
    running = true;
    Promise.all(chunk(todo).map(loadBatch)).then(function () {
      running = false;
      if (queued) { queued = false; run(); }
    });
  }

  var timer = null;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, 400);
  }

  function start() {
    setTimeout(run, FIRST_RUN_DELAY);
    if (typeof MutationObserver === 'function') {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
